<script lang="ts">
  import type { Snippet } from "svelte"
  import type { PopoverMenuItem } from "@kontextwerk/web-sdk/ui/overlay/popover/types"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiArchiveOutline, mdiArchiveArrowUpOutline } from "@mdi/js"
  import {
    archivePropertiesBatch,
    restorePropertiesBatch,
  } from "../../../../../../../../../../api/kehrblatt/property-registry/properties"
  import {
    registryArchiveIssueMessage,
    registryArchiveProcessed,
  } from "../../../../../../../../_shared/_logic/batch-results"
  import { applyPropertyArchiveState, applyPropertyRestoreState } from "./_logic/archiveState"

  let {
    property,
    disabled = false,
    onChanged,
    children,
  }: {
    property: Property | null
    disabled?: boolean
    onChanged: (property: Property) => void
    children: Snippet<[PopoverMenuItem[]]>
  } = $props()

  let saving = $state(false)
  let confirming = $state(false)
  const archived = $derived(Boolean(property?.archived_at))

  const mutate = async () => {
    if (!property || saving || disabled || confirming) return

    const target = property

    if (!archived) {
      let submitted = false
      confirming = true
      try {
        await newApproval({
          title: "Liegenschaft archivieren",
          description:
            "Diese Liegenschaft aus neuen Suchen und Zuordnungen ausblenden? Bestehende Aufträge, Einheiten und Zuordnungen bleiben erhalten.",
          approvalLabel: "Ja, archivieren",
          declineLabel: "Abbrechen",
          approvalCallback: async () => {
            if (submitted) return

            submitted = true
            await perform(target)
          },
        })
      } finally {
        confirming = false
      }

      return
    }

    await perform(target)
  }

  const perform = async (target: Property) => {
    if (
      saving ||
      disabled ||
      property?.id !== target.id ||
      property?.organization_id !== target.organization_id
    )
      return

    saving = true
    try {
      if (target.archived_at) {
        const response = await restorePropertiesBatch([target.id])

        if (!registryArchiveProcessed(response, target.id)) {
          newNotification({
            class: "warning",
            html: registryArchiveIssueMessage(
              response,
              target.id,
              "Liegenschaft wurde nicht wiederhergestellt.",
            ),
          })

          return
        }

        if (property?.id === target.id && property?.organization_id === target.organization_id)
          onChanged(applyPropertyRestoreState(property))

        newNotification({ class: "success", html: "Liegenschaft wiederhergestellt." })
      } else {
        const response = await archivePropertiesBatch([target.id])

        if (!registryArchiveProcessed(response, target.id)) {
          newNotification({
            class: "warning",
            html: registryArchiveIssueMessage(
              response,
              target.id,
              "Liegenschaft wurde nicht archiviert.",
            ),
          })

          return
        }

        if (property?.id === target.id && property?.organization_id === target.organization_id)
          onChanged(applyPropertyArchiveState(property, new Date().toISOString(), "USER_REQUEST"))

        newNotification({ class: "success", html: "Liegenschaft archiviert." })
      }
    } catch (err) {
      console.error(`property_${archived ? "restore" : "archive"}_failed`, err)
      newNotification({
        class: "error",
        html: `Liegenschaft konnte nicht ${archived ? "wiederhergestellt" : "archiviert"} werden.`,
      })
    } finally {
      saving = false
    }
  }
  const actions = $derived<PopoverMenuItem[]>(
    property
      ? [
          {
            id: archived ? "restore" : "archive",
            label: archived ? "Wiederherstellen" : "Archivieren",
            icon: archived ? mdiArchiveArrowUpOutline : mdiArchiveOutline,
            disabled: disabled || saving || confirming,
            tone: archived ? "default" : "danger",
            onClick: mutate,
          },
        ]
      : [],
  )
</script>

{@render children(actions)}
